/**
 * Data Validation Connector configuration component.
 * Specialized configuration for Data Validation connector.
 */

// React Imports
import { useState } from 'react';
import { Input } from '@coordio/ui';

export interface DataValidationConnectorConfigProps {
  settingsJson?: string;
  onChange?: (settingsJson: string) => void;
}

/**
 * Data Validation Connector configuration component.
 */
export const DataValidationConnectorConfig = ({
  settingsJson = '{}',
  onChange,
}: DataValidationConnectorConfigProps) => {
  const [service, setService] = useState<'nppes' | 'address' | 'cms'>('nppes');
  const [apiKey, setApiKey] = useState('');
  const [uspsUserId, setUspsUserId] = useState('');

  const handleServiceChange = (newService: 'nppes' | 'address' | 'cms') => {
    setService(newService);
    const settings: any = {};
    if (newService === 'nppes' || newService === 'cms') {
      settings.service = newService;
      settings.apiKey = apiKey;
    } else if (newService === 'address') {
      settings.service = 'address';
      settings.uspsUserId = uspsUserId;
    }
    onChange?.(JSON.stringify(settings));
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="p-6">
        <h6 className="text-lg font-semibold tracking-tight mb-6">
          Service Configuration
        </h6>

        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-2">
            Select Service
          </p>
          <div className="inline-flex w-full rounded-md border" role="group">
            {(['nppes', 'address', 'cms'] as const).map((val, idx) => {
              const labels: Record<string, string> = {
                nppes: 'NPPES / NPI Registry',
                address: 'Address Validation (USPS)',
                cms: 'CMS Provider Data',
              };
              return (
                <button
                  key={val}
                  type="button"
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                    idx < 2 ? 'border-r' : ''
                  } ${idx === 0 ? 'rounded-l-md' : ''} ${idx === 2 ? 'rounded-r-md' : ''} ${
                    service === val ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
                  }`}
                  onClick={() => handleServiceChange(val)}
                >
                  {labels[val]}
                </button>
              );
            })}
          </div>
        </div>

        {service === 'nppes' || service === 'cms' ? (
          <div className="flex flex-col gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">API Key</label>
              <Input
                type="password"
                className="w-full"
                value={apiKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setApiKey(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.apiKey = e.target.value;
                  settings.service = service;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">
                {service === 'nppes' ? 'NPPES API key (if required)' : 'CMS API key'}
              </p>
            </div>
            {service === 'nppes' && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800/40 dark:bg-blue-950/50 dark:text-blue-300">
                NPPES API is publicly available and may not require an API key. Some features may have rate limits.
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">USPS API User ID</label>
              <Input
                type="text"
                className="w-full"
                value={uspsUserId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setUspsUserId(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.uspsUserId = e.target.value;
                  settings.service = 'address';
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">USPS Web Tools API User ID</p>
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800/40 dark:bg-blue-950/50 dark:text-blue-300">
              USPS Address Validation API requires registration. Obtain your User ID from the USPS Web Tools website.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
